import { IconQuestionMark } from '@tabler/icons-react';

import {
  Avatar,
  Button,
  Group,
  MantineSize,
  Skeleton,
  Stack,
  Text,
  UserCellView,
} from '@optiro/ui';

import { useResourcePlaceholderAssign } from '@/components/ResourcePlaceholderAssign';
import { useAccount } from '@/hooks/useAccount';
import { usePartner } from '@/hooks/usePartners/usePartners';
import { Resource } from '@/types/types';

const ResourceWithCategory = ({
  resourceId,
  resourceableId,
  resourceableType,
  size = 'md',
  category,
}: {
  category?: string;
  resourceId: Resource['id'];
  resourceableId: Resource['resourceable_id'];
  resourceableType: Resource['resourceable_type'];
  size?: MantineSize;
}) => {
  const placeholderAssign = useResourcePlaceholderAssign({
    resourceId: resourceId,
    onSuccess: () => {
      console.log('Successfully assigned!');
    },
  });

  const isAccount = resourceableType === 'Account';
  const isPartner = resourceableType === 'Partner';
  const isPlaceholder = resourceableType === 'ResourcePlaceholder';

  const { data: account, isLoading: accountLoading } = useAccount(resourceableId, {
    enabled: isAccount,
  });

  const { data: partner, isLoading: partnerLoading } = usePartner(resourceableId, {
    enabled: isPartner,
  });

  if (isPlaceholder) {
    return (
      <Group>
        <placeholderAssign.Drawer />

        <Avatar color="gray" radius="xl" size={size}>
          <IconQuestionMark size={size} stroke={1.5} />
        </Avatar>
        <Stack gap={0}>
          <Text size={size}>Pladsholder Ressource</Text>
          <Text size="xs" c="dimmed" fw={500}>
            {category || 'Ingen kategori'}
          </Text>
        </Stack>

        <Button size="xs" variant="outline" color="dark" onClick={placeholderAssign.open}>
          Tildel ressource
        </Button>
      </Group>
    );
  }

  const isLoading = accountLoading || partnerLoading;
  const resource = resourceableType === 'Account' ? account : partner;

  // Determine category: passed prop > resource category > fallback
  const displayCategory =
    category ||
    (isPartner
      ? partner?.company_account_category?.name
      : account?.company_account_category?.name) ||
    'Ingen kategori';

  if (isLoading || !resource) {
    return (
      <Group>
        <Skeleton height={50} circle mb="xl" />
        <Stack>
          <Text size={size}>
            <Skeleton height={8} radius="xl" />
          </Text>
          <Text size="xs" c="dimmed" fw={500}>
            <Skeleton height={8} radius="xl" />
          </Text>
        </Stack>
      </Group>
    );
  }

  return (
    <UserCellView
      name={resource.name}
      subtitle={displayCategory}
      avatarVariant={isPartner ? 'partner' : 'employee'}
      photoUrl={isAccount ? account?.photo?.thumb?.url : undefined}
    />
  );
};

export { ResourceWithCategory };
